/**
 * Fractional ordering (docs/07 C1).
 *
 * Rows, columns and sheets carry a fractional `ord` key (LexoRank-style
 * string, via the `fractional-indexing` package). Rendering sorts by
 * `(ord, id)` — the id tie-break makes the order total and deterministic even
 * when concurrent inserts between the same neighbors produce equal keys.
 *
 * There are NO ordered arrays anywhere: a move is one LWW write to one key,
 * so concurrent moves can never duplicate a row, and a permanent reorder
 * (rewrite of all `ord` keys) can never resurrect a concurrently-deleted one.
 */
import { generateKeyBetween, generateNKeysBetween } from "fractional-indexing";
/**
 * Total, deterministic order: primary lexicographic compare of `ord`,
 * tie-break by `id` (C1 — equal keys from concurrent same-gap inserts).
 */
export function compareOrd(a, b) {
    if (a.ord < b.ord)
        return -1;
    if (a.ord > b.ord)
        return 1;
    if (a.id < b.id)
        return -1;
    if (a.id > b.id)
        return 1;
    return 0;
}
/** Sorted copy of `entries` by `(ord, id)`. Input is not mutated. */
export function sortOrdered(entries) {
    return entries.slice().sort(compareOrd);
}
/**
 * Key strictly between two neighbor keys (`null` = start/end of list).
 *
 * Degenerate case: after concurrent same-gap inserts two neighbors can carry
 * EQUAL (or, via id tie-break, inverted) keys. A strictly-between key cannot
 * exist then, so the new key is placed immediately after `a` — the entry lands
 * adjacent to the group, order stays total, and convergence is unaffected.
 * Prefer `keyAtIndex` where the whole sorted key list is available.
 */
export function keyBetween(a, b) {
    if (a !== null && b !== null && a >= b) {
        return generateKeyBetween(a, null);
    }
    return generateKeyBetween(a, b);
}
/**
 * Key for inserting at position `index` (0..length) of a list whose current
 * keys, sorted, are `sortedOrds`. Handles runs of equal keys by widening the
 * upper bound to the next strictly-greater key, so it never violates the
 * `lower < upper` requirement of fractional keys.
 */
export function keyAtIndex(sortedOrds, index) {
    const i = Math.max(0, Math.min(index, sortedOrds.length));
    const lower = i > 0 ? sortedOrds[i - 1] : null;
    let upper = null;
    for (let j = i; j < sortedOrds.length; j++) {
        const candidate = sortedOrds[j];
        if (lower === null || candidate > lower) {
            upper = candidate;
            break;
        }
    }
    return generateKeyBetween(lower, upper);
}
/**
 * Key(s) for inserting `count` entries at position `index` (0..length) of
 * `sorted` (entries sorted by `(ord, id)`). ONE pass: the whole batch's keys
 * come from a single `generateNKeysBetween` call, so an N-row import is O(N)
 * key work over ONE read of the current order — not N reads (F-90; the
 * per-row `addRow` loop is O(n²), measured 5/8/25/93 ms at 250/500/1k/2k).
 *
 * Normal case: `count` fresh keys strictly between the neighbors, no rewrites.
 *
 * Equal-key runs (possible after concurrent same-gap inserts): when the
 * insertion point falls INSIDE a run of equal ord keys, no key can sort
 * strictly between two equal keys, so naive between-keys would land the batch
 * after the whole run — up to run-length positions below where the user asked.
 * Instead, the run members at/after the insertion point get fresh keys too (a
 * bounded mini-rebalance, LWW-safe exactly like `rebalance`/C3), so the batch
 * lands exactly at `index`. The rewrite keys are drawn from the SAME generated
 * sequence, after the inserted ones — which is what keeps the batch ahead of
 * the run it displaced.
 */
export function planInsertManyAtIndex(sorted, index, count) {
    if (count <= 0)
        return { ords: [], rewrites: [] };
    const i = Math.max(0, Math.min(index, sorted.length));
    const lower = i > 0 ? sorted[i - 1].ord : null;
    // Find the first strictly-greater key at or after the insertion point;
    // everything before it (the tail of an equal-key run) must be re-keyed.
    let runEnd = i;
    let upper = null;
    while (runEnd < sorted.length) {
        const candidate = sorted[runEnd].ord;
        if (lower === null || candidate > lower) {
            upper = candidate;
            break;
        }
        runEnd++;
    }
    const run = sorted.slice(i, runEnd);
    const keys = generateNKeysBetween(lower, upper, count + run.length);
    return {
        ords: keys.slice(0, count),
        rewrites: run.map((entry, k) => ({ id: entry.id, ord: keys[count + k] })),
    };
}
/**
 * Single-entry form of `planInsertManyAtIndex` — the shape every single insert
 * (`addRow`, `addColumn`, `moveRow`, `moveColumn`) wants.
 */
export function planInsertAtIndex(sorted, index) {
    const plan = planInsertManyAtIndex(sorted, index, 1);
    return { ord: plan.ords[0], rewrites: plan.rewrites };
}
/**
 * Fresh, evenly-spaced keys for a whole sheet — used by the explicit
 * "Reorder rows permanently" command (C3). `idsInOrder` is the desired final
 * order; each id gets a brand-new key. Applied in ONE transaction by
 * `BoardDoc.permanentReorder`.
 */
export function rebalance(idsInOrder) {
    const keys = generateNKeysBetween(null, null, idsInOrder.length);
    return idsInOrder.map((id, i) => ({ id, ord: keys[i] }));
}
//# sourceMappingURL=order.js.map